import moment from 'moment'
import Moment from 'moment-timezone'
import { AvailabilitySlot } from '@dialogue/timekeeper'
import type { DateAvailabilities, ProviderAvailability, ProviderResponse } from '@dialogue/silkroad'
import { DATE_FORMAT } from 'APP/Redux/AppointmentBookingRedux'
import {
  PART_OF_DAY_END_HOUR,
  PARTS_OF_DAY,
  PARTS_OF_DAY_MAX,
} from 'APP/Components/AppointmentBooking/constants'
import {
  AvailabilitiesPerPartsOfDay,
  TransformContext,
  TransformResultMap,
  TransformStrategy,
} from 'APP/Components/AppointmentBooking/utils/types'
import { logger } from 'APP/Lib/Datadog'
import type { Provider } from 'APP/Store/ProviderBooking/types'

export type AvailabilitiesByDay = Record<string, AvailabilitySlot[]>
// Keyed by Silkroad v3's date label (YYYY-MM-DD).
export type AvailabilitiesPerPartsOfDayByDate = Record<string, AvailabilitiesPerPartsOfDay>

/**
 * Transforms a list of availabilities into a structure supported by the UI
 * and MX. This transformation is necessary as TK returns a flat list of
 * availabilities for the time range, but the UI either wants parts of the
 * day (e.g., morning, afternoon, evening) or days (e.g., 2026-03-26, 2026-03-27)
 *
 * TODO: in the future both Provider and Time based booking could be unified
 * and this could accept instead a BOOKING_TYPE enum
 */
export const transformAvailabilities = <T extends TransformStrategy>(
  availabilities: AvailabilitySlot[],
  strategy: T,
  context: TransformContext = {}
): TransformResultMap[T] => {
  switch (strategy) {
    case TransformStrategy.BY_PART_OF_DAY:
      return _transformByPartOfDay(availabilities, context) as TransformResultMap[T]
    case TransformStrategy.BY_DAY:
      return _transformByDay(availabilities) as TransformResultMap[T]
    default:
      const error = new Error(`Unknown transformation strategy: ${strategy}`)
      logger.error(`Unknown transformation strategy: ${strategy}`, {}, error)
      throw error
  }
}

const _transformByPartOfDay = (
  availabilities: AvailabilitySlot[],
  context: TransformContext
): AvailabilitiesPerPartsOfDay => {
  const timezone = context.timezone || moment.tz.guess()

  const endAt = context.endAt || moment().add(1, 'week')

  const processed: AvailabilitiesPerPartsOfDay = {
    [PARTS_OF_DAY.MORNING]: [],
    [PARTS_OF_DAY.AFTERNOON]: [],
    [PARTS_OF_DAY.EVENING]: [],
  }

  for (const availability of availabilities) {
    if (
      processed[PARTS_OF_DAY.MORNING].length >= PARTS_OF_DAY_MAX &&
      processed[PARTS_OF_DAY.AFTERNOON].length >= PARTS_OF_DAY_MAX &&
      processed[PARTS_OF_DAY.EVENING].length >= PARTS_OF_DAY_MAX
    ) {
      break
    }

    const startAt = Moment.tz(availability.start_at, timezone)

    if (startAt.isAfter(endAt)) {
      continue
    }

    const startHour = startAt.hour()

    let part: PARTS_OF_DAY | undefined

    switch (true) {
      case startHour < PART_OF_DAY_END_HOUR.MORNING:
        part = PARTS_OF_DAY.MORNING
        break
      case startHour < PART_OF_DAY_END_HOUR.AFTERNOON:
        part = PARTS_OF_DAY.AFTERNOON
        break
      default:
        part = PARTS_OF_DAY.EVENING
    }

    if (processed[part].length >= PARTS_OF_DAY_MAX) {
      continue
    }
    processed[part].push(availability)
  }

  return processed
}

const _transformByDay = (availabilities: AvailabilitySlot[]): AvailabilitiesByDay => {
  return availabilities.reduce((acc, slot) => {
    const slotDay = moment(slot.start_at).format(DATE_FORMAT)
    if (!acc[slotDay]) {
      acc[slotDay] = []
    }
    acc[slotDay].push(slot)
    return acc
  }, {} as AvailabilitiesByDay)
}

type TimedProviderAvailability = ProviderAvailability & { start_at: string; end_at: string }

// Only a provider-based availability can come without times; a time-based one always has them.
const _isTimed = (availability: ProviderAvailability): availability is TimedProviderAvailability =>
  !!availability.start_at && !!availability.end_at

const _toAvailabilitySlot = (availability: TimedProviderAvailability): AvailabilitySlot => ({
  start_at: availability.start_at,
  end_at: availability.end_at,
  provider_id: availability.provider_id,
  provider_matching_attributes: availability.provider_matching_attributes,
})

const _toAvailabilitySlots = (availabilities: ProviderAvailability[] = []): AvailabilitySlot[] =>
  availabilities.filter(_isTimed).map(_toAvailabilitySlot)

/**
 * Maps Silkroad v3's days to the part-of-day grouping the time-based UI renders, one per
 * date v3 labels them with. v3 only returns the days that have times, and already caps and
 * sorts each part of each day, so nothing is re-capped here.
 */
export const groupDateAvailabilitiesByDate = (
  days: DateAvailabilities[]
): AvailabilitiesPerPartsOfDayByDate =>
  Object.fromEntries(
    days.map((day) => [
      day.date,
      {
        [PARTS_OF_DAY.MORNING]: _toAvailabilitySlots(day.morning),
        [PARTS_OF_DAY.AFTERNOON]: _toAvailabilitySlots(day.afternoon),
        [PARTS_OF_DAY.EVENING]: _toAvailabilitySlots(day.evening),
      },
    ])
  )

/**
 * Flattens Silkroad v3 day-grouped availabilities into AvailabilitySlot[].
 */
export const flattenDateAvailabilities = (days: DateAvailabilities[]): AvailabilitySlot[] =>
  days
    .flatMap((day) => Object.values(PARTS_OF_DAY).flatMap((part) => day[part] ?? []))
    .filter(_isTimed)
    .map(_toAvailabilitySlot)

/**
 * Silkroad v3's providers map, keyed by provider id, as the store's providers.
 */
export const mapSilkroadProviders = (
  providers: Record<string, ProviderResponse>
): Record<number, Provider> =>
  Object.fromEntries(
    Object.values(providers).map((provider) => [
      provider.id,
      {
        id: provider.id,
        givenName: provider.givenName ?? '',
        familyName: provider.familyName ?? '',
        picture: provider.picture ?? undefined,
        mainSpecialization: provider.mainSpecialization ?? undefined,
        communication_languages: provider.communication_languages ?? [],
        licenses: provider.licenses ?? [],
      } as Provider,
    ])
  )
